/**
 * The Extinction (docs/fossil-eras-plan.md R2): the button that ends an era,
 * and the confirm that has to be got past first.
 *
 * The button only exists once the era has laid the threshold, and it says
 * what it would pay so the trade-off is legible before the dialog opens. The
 * confirm is irreversible, so it spells out what is lost as well as kept.
 */

import { extinctionThreshold, fossilScale } from '../data/eras';
import {
  canExtinct,
  eraModifier,
  expedition,
  expeditionMultiplier,
  fossilsFor,
  fossilsOnExtinction,
  eraOffer,
} from '../game/rebirth';
import { speciesName, spriteIdFor } from '../game/inventory';
import { BACKGROUNDS, mountUrl } from './sprites';
import { DINOS } from '../data/dinos';
import { EXPEDITIONS } from '../data/eras';
import type { EraModifier } from '../data/eras';

/** Eras a player must have ended before expeditions are offered. */
const EXPEDITION_FROM_ERA = 2;
import { formatDuration, formatEggs } from '../game/numbers';
import { bindTooltip } from './tooltip';
import type { GameState } from '../game/state';

export interface ExtinctionView {
  update(state: GameState): void;
  /** True while the confirm is open, so the yard can hold off spawning things. */
  readonly open: boolean;
  /** End the era now, keeping nothing — what a Sprint does when its time is up. */
  callIt(): void;
}

/** What the player chose on the way out. */
export interface ExtinctionChoice {
  /** Dinosaur ids whose fossils were excavated. */
  excavated: string[];
  /** The modifier the next era plays under, or null for a plain one. */
  eraId: string | null;
  /** The expedition the next era is run as, or null. */
  expedition: string | null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** The next round number of fossils, and what the era must lay to reach it. */
function nextStep(eggsThisEra: number, era: number): { fossils: number; at: number } {
  const next = fossilsFor(eggsThisEra, era) + 1;
  return { fossils: next, at: next ** 3 * fossilScale(era) };
}

export function createExtinctionView(
  root: HTMLElement,
  eraHost: HTMLElement,
  onExtinct: (choice: ExtinctionChoice) => void,
): ExtinctionView {
  // The panel under the yard: what the era is chasing, and — only once it is
  // available — the button that ends it. Before the threshold it names the
  // extinction and nothing else; fossils are not mentioned until they can
  // actually be had. Without it the whole mechanic is invisible until the
  // moment it unlocks, and a player has no way to see they are working
  // towards anything.
  const chase = el('div', 'extinction__panel');
  chase.hidden = true;
  const icon = el('img', 'extinction__icon');
  icon.src = '/art/icons/ach/eras-fossils.png';
  icon.alt = '';
  icon.width = 48;
  icon.height = 48;
  const chaseBody = el('div', 'extinction__chase');
  const head = el('p', 'extinction__head');
  // The bar carries its own figures: what has been laid over the filled part
  // and what is left over the empty part. They are drawn twice and each copy
  // clipped to one side of the fill, so the text is dark over bone and light
  // over the track without needing an outline.
  const bar = el('div', 'extinction__bar');
  const fill = el('i', 'extinction__fill');
  const lightText = el('span', 'extinction__figures');
  const darkText = el('span', 'extinction__figures extinction__figures--over');
  darkText.setAttribute('aria-hidden', 'true');
  const figures = [lightText, darkText].map((layer) => {
    const laid = el('span', '');
    const left = el('span', '');
    layer.append(laid, left);
    return { laid, left };
  });
  bar.append(fill, lightText, darkText);
  chaseBody.append(head, bar);

  // Not rendered at all until the extinction is available: a disabled
  // button would only say no, and the headline already says when.
  const button = el('button', 'extinction__button');
  button.type = 'button';
  button.hidden = true;
  const pay = el('span', 'extinction__pay');
  button.append(el('span', 'extinction__label', 'Trigger Extinction'), pay);
  chase.append(icon, chaseBody, button);

  // Which era this is, and under what rules. Both are announced in a toast
  // when the era begins and then never again, so mid-era there is no way to
  // check whether clicking is worth anything — or how long a Sprint has left.
  // It lives in the eggs panel, under the rates: the era is part of the
  // economy's state, and the rules it sets are what the rates are read under.
  const era = el('p', 'stat--era');
  era.hidden = true;
  era.tabIndex = 0;
  bindTooltip(era, () => {
    const state = viewState;
    const mod = state ? eraModifier(state) : null;
    const trip = state ? expedition(state) : null;
    const lines = [];
    if (mod) lines.push({ text: mod.name }, { text: mod.description, muted: true });
    if (trip) lines.push({ text: trip.name }, { text: trip.description, muted: true });
    if (lines.length === 0) lines.push({ text: 'A plain era, with no modifier.', muted: true });
    return lines;
  });

  eraHost.replaceWith(era);
  root.append(chase);

  // ---- the confirm --------------------------------------------------------
  // The end of an era takes the whole screen (docs/mockups/extinction-
  // dialog.html, C). It is the biggest moment in the game, and it used to be
  // three lists in a box over the yard, each scrolling inside a box that
  // scrolled too, all in the pixel face. Now it fades from the era's own
  // painting into the dark, the payout leads, the body scrolls as one, and a
  // line over the buttons says what has been picked. On a wide screen what is
  // kept and lost sits in a column beside the choices.
  // Still a .modal, so anything that asks whether a dialog is open finds it.
  const backdrop = el('div', 'modal xscreen');
  backdrop.hidden = true;
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'xscreen-title');

  const hero = el('div', 'xscreen__hero');
  const title = el('h2', 'xscreen__title');
  title.id = 'xscreen-title';
  const payout = el('div', 'xscreen__payout');
  const payIcon = el('img', 'xscreen__payicon');
  payIcon.src = `${import.meta.env.BASE_URL}art/icons/ach/eras-fossils.png`;
  payIcon.alt = '';
  payIcon.width = 48;
  payIcon.height = 48;
  const payCount = el('p', 'xscreen__fossils');
  const payNote = el('p', 'xscreen__small', '+5% production each, in every era after this one');
  const payText = el('div', '');
  payText.append(payCount, payNote);
  payout.append(payIcon, payText);
  hero.append(title, payout);

  // Names go with the herd; only the excavated dinosaur's is kept, on its
  // Museum plaque.
  const listBox = (heading: string, items: string[], kind: string): HTMLElement => {
    const box = el('div', `xscreen__kl xscreen__kl--${kind}`);
    const list = el('ul', '');
    for (const item of items) list.append(el('li', '', item));
    box.append(el('h3', '', heading), list);
    return box;
  };
  const keepLose = el('div', 'xscreen__keeplose');
  keepLose.append(
    listBox(
      'You keep',
      [
        'Fossils and Museum perks',
        'Achievements and lifetime records',
        'The excavated name, on its plaque',
      ],
      'keep',
    ),
    listBox(
      'You lose',
      ['Every dinosaur and its name', 'Eggs and upgrades', 'Shards and evolutions'],
      'lose',
    ),
  );
  const more = el('p', 'xscreen__small');
  const side = el('div', 'xscreen__side');
  side.append(keepLose, more);

  const sectionLabel = (text: string): HTMLElement => {
    const p = el('p', 'xscreen__label', text);
    p.append(el('span', '', 'Optional'));
    return p;
  };
  // The excavation: whose fossil is dug out of the ash and mounted in the
  // Museum. It carries no production — choosing it is the emotional beat of
  // a rebirth, and it stopped being one while the answer was computable. Each
  // is shown as the mount it would become.
  const excavateTitle = sectionLabel('Excavate one for the Museum');
  const excavateList = el('div', 'xscreen__digs');
  let excavatedId: string | null = null;
  let excavatedLabel = '';

  // The next era's weather, three to pick from so a bad draw is avoidable,
  // each shown as its painting. Picking none plays a plain era.
  const eraTitle = sectionLabel('The next era');
  const eraList = el('div', 'xscreen__eras');
  let eraId: string | null = null;
  let offered: EraModifier[] = [];

  // Expeditions: a rule for the whole era in exchange for a bigger payout.
  // Offered only once the player has been round the loop a few times, since
  // they are a reason to play differently rather than a first-era choice.
  const tripTitle = sectionLabel('Run it as an expedition');
  const tripList = el('div', 'xscreen__trips');
  let tripId: string | null = null;
  let tripsOpen = false;

  const picks = el('div', 'xscreen__picks');
  picks.append(excavateTitle, excavateList, eraTitle, eraList, tripTitle, tripList);
  const cols = el('div', 'xscreen__cols');
  cols.append(side, picks);
  const scroll = el('div', 'xscreen__scroll');
  scroll.append(cols);

  const chosen = el('p', 'xscreen__chosen');
  const actions = el('div', 'modal__actions');
  const go = el('button', 'modal__button modal__button--danger', 'End the era');
  go.type = 'button';
  const cancel = el('button', 'modal__button modal__button--quiet', 'Not yet');
  cancel.type = 'button';
  actions.append(cancel, go);
  const foot = el('div', 'xscreen__foot');
  foot.append(chosen, actions);

  const inner = el('div', 'xscreen__inner');
  inner.append(hero, scroll, foot);
  backdrop.append(inner);
  root.append(backdrop);

  let open = false;
  let viewState: GameState | null = null;

  /** One pick tile; `on` toggles it and says whether it is now the choice. */
  function pickButton(className: string, children: Node[], on: () => boolean): HTMLButtonElement {
    const b = el('button', `xscreen__pick ${className}`);
    b.type = 'button';
    b.setAttribute('aria-pressed', 'false');
    b.append(...children);
    b.addEventListener('click', () => {
      const chosenNow = on();
      for (const other of b.parentElement?.children ?? []) {
        const pressed = other === b && chosenNow;
        other.classList.toggle('xscreen__pick--on', pressed);
        other.setAttribute('aria-pressed', String(pressed));
      }
      renderChosen();
    });
    return b;
  }

  /** "Excavating "Spike" · Night next · no expedition", over the buttons. */
  function renderChosen(): void {
    const parts: Node[] = [];
    const add = (lead: string, bold: string, tail: string): void => {
      if (parts.length > 0) parts.push(document.createTextNode(' · '));
      parts.push(document.createTextNode(lead), el('b', '', bold), document.createTextNode(tail));
    };
    if (excavatedId) add('Excavating ', excavatedLabel, '');
    else add('', 'Nothing', ' excavated');
    const era = offered.find((e) => e.id === eraId);
    if (era) add('', era.name, ' next');
    else add('A ', 'plain', ' era next');
    if (tripsOpen) {
      const trip = EXPEDITIONS.find((t) => t.id === tripId);
      if (trip) add('', trip.name, ' expedition');
      else add('', 'no', ' expedition');
    }
    chosen.replaceChildren(...parts);
  }

  function show(): void {
    const state = viewState;
    if (!state || !canExtinct(state)) return;
    const fossils = fossilsOnExtinction(state);
    title.textContent = `The end of Era ${state.era + 1}`;
    payCount.textContent = `${fossils} fossil${fossils === 1 ? '' : 's'}`;
    const step = nextStep(state.eggsThisEra, state.era);
    // Times the expedition, as the payout above and the panel under the yard are.
    const later = Math.floor(step.fossils * expeditionMultiplier(state));
    more.textContent = `Staying until ${formatEggs(step.at)} eggs this era would pay ${later}.`;
    // The screen fades from the painting the era was played against.
    const painting = BACKGROUNDS[eraModifier(state)?.background ?? 'default'];
    backdrop.style.setProperty('--era-art', `url('${import.meta.env.BASE_URL}art/${painting}')`);

    // Every hatched tier, named ones first — those are the ones a
    // player will actually miss.
    excavatedId = null;
    const hatched = DINOS.filter((d) => (state.dinos[d.id] ?? 0) > 0);
    hatched.sort((a, b) => {
      const an = state.names[a.id] ? 0 : 1;
      const bn = state.names[b.id] ? 0 : 1;
      return an - bn || (state.dinos[b.id] ?? 0) - (state.dinos[a.id] ?? 0);
    });
    excavateList.replaceChildren(
      ...hatched.map((def) => {
        const pet = state.names[def.id];
        const species = speciesName(def, state);
        const level = state.dinos[def.id] ?? 0;
        const label = pet ? `"${pet}"` : species;
        // A pet named after its own species needs the species only once.
        const note =
          pet && pet.trim().toLowerCase() !== species.toLowerCase()
            ? `${species} · ${level}`
            : `Lvl ${level}`;
        const mount = el('img', '');
        mount.src = mountUrl(spriteIdFor(def, state));
        mount.alt = '';
        mount.width = 64;
        mount.height = 64;
        return pickButton(
          'xscreen__dig',
          [mount, el('b', '', label), el('small', '', note)],
          () => {
            excavatedId = excavatedId === def.id ? null : def.id;
            excavatedLabel = label;
            return excavatedId !== null;
          },
        );
      }),
    );
    excavateTitle.hidden = hatched.length === 0;
    excavateList.hidden = hatched.length === 0;

    // Eras: three drawn without repeats; picking none plays a plain one.
    eraId = null;
    offered = eraOffer(state);
    eraList.replaceChildren(
      ...offered.map((era) => {
        const art = el('img', '');
        art.src = `${import.meta.env.BASE_URL}art/${BACKGROUNDS[era.background ?? 'default']}`;
        art.alt = '';
        const text = el('span', 'xscreen__eratext');
        text.append(el('b', '', era.name), el('span', '', era.description));
        return pickButton('xscreen__era', [art, text], () => {
          eraId = eraId === era.id ? null : era.id;
          return eraId !== null;
        });
      }),
    );

    // Expeditions from the third era on: by then the ordinary loop is known.
    tripId = null;
    tripsOpen = state.era >= EXPEDITION_FROM_ERA;
    tripTitle.hidden = !tripsOpen;
    tripList.hidden = !tripsOpen;
    if (tripsOpen) {
      tripList.replaceChildren(
        ...EXPEDITIONS.map((trip) =>
          pickButton(
            'xscreen__trip',
            [el('b', '', trip.name), el('span', '', trip.description)],
            () => {
              tripId = tripId === trip.id ? null : trip.id;
              return tripId !== null;
            },
          ),
        ),
      );
    }

    renderChosen();
    scroll.scrollTop = 0;
    open = true;
    backdrop.hidden = false;
    cancel.focus();
  }

  function close(): void {
    if (!open) return;
    open = false;
    backdrop.hidden = true;
  }

  button.addEventListener('click', show);
  cancel.addEventListener('click', close);
  window.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });
  go.addEventListener('click', () => {
    const choice = { excavated: excavatedId ? [excavatedId] : [], eraId, expedition: tripId };
    close();
    onExtinct(choice);
  });

  let lastShown: boolean | null = null;
  let lastPay = '';
  let lastHead = '';
  let lastFigures = '';
  let lastFill = -1;
  let lastEra = '';

  return {
    get open() {
      return open;
    },
    callIt() {
      close();
      onExtinct({ excavated: [], eraId: null, expedition: null });
    },
    update(state) {
      viewState = state;
      const eraText = eraLine(state, Date.now());
      if (eraText !== lastEra) {
        lastEra = eraText;
        era.textContent = eraText;
        era.hidden = eraText === '';
        era.setAttribute('aria-label', eraText === '' ? '' : `This era: ${eraText}`);
      }
      // Only once there is something to show: a player on their first
      // hundred eggs does not need to hear about the end of the world.
      chase.hidden = state.totalEggs <= 0;
      const ready = canExtinct(state);
      if (ready !== lastShown) {
        lastShown = ready;
        button.hidden = !ready;
        chase.classList.toggle('extinction__panel--ready', ready);
        if (!ready) close();
      }
      const read = chaseReadout(state);
      const headKey = `${read.fossils}|${read.at}`;
      if (headKey !== lastHead) {
        lastHead = headKey;
        const at = el('b', '', formatEggs(read.at));
        if (read.fossils === null) {
          head.replaceChildren('Extinction available at ', at, ' eggs');
        } else {
          const goal = el('span', 'extinction__goal', `${read.fossils} Fossils`);
          head.replaceChildren(goal, ' at ', at, ' eggs');
        }
      }
      const laidText = `${formatEggs(state.eggsThisEra)} laid`;
      const leftText = `${formatEggs(read.at - state.eggsThisEra)} to go`;
      const figuresKey = `${laidText}|${leftText}`;
      if (figuresKey !== lastFigures) {
        lastFigures = figuresKey;
        for (const f of figures) {
          f.laid.textContent = laidText;
          f.left.textContent = leftText;
        }
      }
      // A tenth of a percent is under a pixel on any bar this game draws.
      const pct = Math.round(read.frac * 1000) / 10;
      if (pct !== lastFill) {
        lastFill = pct;
        fill.style.width = `${pct}%`;
        lightText.style.clipPath = `inset(0 0 0 ${pct}%)`;
        darkText.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      }
      if (!ready) return;
      const fossils = fossilsOnExtinction(state);
      // "Earn 13 Fossils", not "13 Fossils": a bare count on the button
      // reads like a price being charged rather than the payout it is.
      const noun = `${fossils} Fossil${fossils === 1 ? '' : 's'}`;
      const text = `Earn ${noun}`;
      if (text !== lastPay) {
        lastPay = text;
        pay.textContent = text;
        button.setAttribute('aria-label', `Trigger an extinction to earn ${noun.toLowerCase()}`);
      }
    },
  };
}

/**
 * The era in one line: its number, its modifier and any expedition, with a
 * Sprint's time left. Empty in a first, plain era — a player who has never
 * seen an extinction has no use for the word "era" yet.
 */
export function eraLine(state: GameState, now: number): string {
  const parts: string[] = [];
  if (state.era > 0) parts.push(`Era ${state.era + 1}`);
  const mod = eraModifier(state);
  if (mod) parts.push(mod.name);
  const trip = expedition(state);
  if (trip) {
    parts.push(trip.name);
    if (trip.endsAfterSeconds !== undefined) {
      const left = trip.endsAfterSeconds - (now - state.eraStartedAt) / 1000;
      parts.push(`${formatDuration(Math.max(0, left))} left`);
    }
  }
  return parts.join(' · ');
}

/**
 * What the panel under the yard is chasing. Before the threshold that is the
 * extinction itself, and `fossils` is null — nothing is said about fossils
 * until they can be had. After it, the next fossil: how many the extinction
 * would then pay (at the expedition's rate) and the eggs it comes at.
 *
 * `frac` runs from zero rather than from the last fossil, so the bar agrees
 * with the "laid" and "to go" written on it; the cost is that it starts each
 * new fossil somewhat over half full, which the cube root makes unavoidable.
 */
export function chaseReadout(state: GameState): {
  fossils: number | null;
  at: number;
  frac: number;
} {
  const frac = (at: number) => Math.min(1, Math.max(0, state.eggsThisEra / at));
  if (!canExtinct(state)) {
    const at = extinctionThreshold(state.era);
    return { fossils: null, at, frac: frac(at) };
  }
  const next = fossilsFor(state.eggsThisEra, state.era) + 1;
  const at = next ** 3 * fossilScale(state.era);
  return { fossils: Math.floor(next * expeditionMultiplier(state)), at, frac: frac(at) };
}
